import { observer } from "mobx-react";
import React, { useState } from "react";
import {
  Stack,
  Modal,
  Button,
  Text,
  Accordion,
  rem,
  Alert,
  Divider,
} from "@mantine/core";
import {
  IconArrowBigDownFilled,
  IconCirclePlus,
  IconCoinBitcoin,
  IconInfoCircle,
  IconKey,
  IconPlus,
} from "@tabler/icons-react";
import { Cubby } from "../../stores/Cubby";
import { MIN_CHANNEL_SIZE_SATS, MODALS } from "../../helpers/constants";

import { TextWithInfo } from "./textWithInfo";
import AddressAccordionSection from "../interactiveData/addressAccordianSection";
import { TextWithInfoAndIcon } from "../dataDisplay/textWithInfoAndIcon";
import { ARTICLES } from "../../docs/docsIndex";

const DepositBtc = observer(({ cubby }: { cubby: Cubby }) => {
  const [isCreatingAddress, setIsCreatingAddress] = useState(false);
  const onchainAddresses = cubby.onchainAddresses?.data || [];

  const p2trAddresses = onchainAddresses.filter((addr) => addr.kind === "p2tr");
  const p2wpkhAddresses = onchainAddresses.filter(
    (addr) => addr.kind === "p2wpkh"
  );

  const handleCreateNewAddress = () => {
    setIsCreatingAddress(true);
    cubby.cubbyLife("make_onchain_addresses");
    setTimeout(() => {
      setIsCreatingAddress(false);
    }, 2000);
  };

  return (
    <Modal
      opened={cubby.currentModal === MODALS.DEPOSIT_BTC}
      onClose={() => {
        cubby.setModal(null);
      }}
      title={
        <TextWithInfoAndIcon
          text="Deposit BTC"
          articleSlug={ARTICLES.DEPOSIT_BTC.slug}
          icon={<IconArrowBigDownFilled size={24} />}
          cubby={cubby}
          textSize="lg"
        />
      }
    >
      <Stack>
        <Text size="sm">
          Onchain deposits usually confirm in 10-30 minutes, but may take
          longer.
        </Text>

        <Alert
          variant="light"
          color="blue"
          title="Be careful with your deposit addresses"
          icon={<IconInfoCircle />}
        >
          Rizful nodes with zero (0) BTC balance and zero (0) open channels will
          eventually be deleted. Any funds sent to a deleted node will be lost
          forever. Do not use BTC addresses generated on this screen as
          long-term, stable "pay to" addresses.
        </Alert>
        <Alert
          variant="light"
          color="blue"
          title="Authenticator app required for withdrawals"
          icon={<IconInfoCircle />}
        >
          When your BTC balance reaches 100,000 satoshis, you will be required
          to set up an authenticator app to generate one-time security codes for
          all withdrawals.
        </Alert>

        <Alert
          variant="light"
          color="blue"
          title="How much should I deposit?"
          icon={<IconInfoCircle />}
        >
          For each Lightning channel you want to open, we suggest a minimum
          deposit of {cubby.minimumOnchainFundsToOpenChannel.toLocaleString()}{" "}
          sats. This will allow you to open a channel at the{" "}
          {MIN_CHANNEL_SIZE_SATS.toLocaleString()} minimum channel capacity, and
          still retain some leftover BTC to pay closure fees.
        </Alert>
        <Divider my="xs" />
        <Button
          onClick={handleCreateNewAddress}
          leftSection={<IconPlus />}
          size="sm"
          variant="default"
          aria-label="make btc address"
          disabled={isCreatingAddress}
        >
          {isCreatingAddress ? "Creating Address..." : "Make New BTC Address"}
        </Button>
        <Text size="sm">
          To improve your privacy, make a new address each time you want to
          receive a payment.
        </Text>

        {isCreatingAddress && (
          <Text c="dimmed" size="sm">
            New address creation in progress, click below to copy an address.
          </Text>
        )}

        {onchainAddresses.length > 0 && (
          <Accordion variant="contained">
            <AddressAccordionSection
              addresses={p2trAddresses}
              kind="p2tr"
              title="New-style Addresses"
              icon={
                <IconCoinBitcoin
                  style={{
                    color: "var(--mantine-color-teal-6)",
                    width: rem(20),
                    height: rem(20),
                  }}
                />
              }
              description="New-style addresses are more efficient, but don't work with all wallets."
              infoLink="https://docs.megalithic.me/new-style-addresses"
            />
            <AddressAccordionSection
              addresses={p2wpkhAddresses}
              kind="p2wpkh"
              title="Old-style Addresses"
              icon={
                <IconCoinBitcoin
                  style={{
                    color: "var(--mantine-color-blue-6)",
                    width: rem(20),
                    height: rem(20),
                  }}
                />
              }
              description="Use these addresses to send funds from legacy services like Binance, Gemini, and PayPal."
              infoLink="https://docs.megalithic.me/old-style-addresses"
            />
          </Accordion>
        )}
      </Stack>
    </Modal>
  );
});

export default DepositBtc;
